import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API_BASE_URL } from '../../config/api';

interface SupportCount {
  channel?: string;
  category?: string;
  type?: string;
  tag?: string;
  count: number;
}

interface SupportDay {
  date: string;
  created: number;
  closed: number;
}

interface SupportData {
  days: number;
  configured: boolean;
  error: string | null;
  account: string | null;
  created: number;
  closed: number;
  stillOpen: number;
  openNow: number | null;
  csat: { surveys: number; average: number | null; rate: number | null };
  channels: SupportCount[];
  categories: SupportCount[];
  tagTypes: SupportCount[];
  tags: SupportCount[];
  series: SupportDay[];
}

const RANGES = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const formatDay = (value: string) => {
  const date = new Date(`${value}T00:00:00Z`);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatChannel = (value: string) => value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const percent = (value: number | null) => (value == null ? '—' : `${Math.round(value * 100)}%`);

const CHANNEL_COLORS: Record<string, string> = {
  email: '#2563eb',
  chat: '#16a34a',
  sms: '#f59e0b',
  facebook: '#1877f2',
  instagram: '#db2777',
  phone: '#7c3aed',
  api: '#64748b',
  other: '#94a3b8',
};

const PALETTE = ['#2563eb', '#7c3aed', '#0d9488', '#ea580c', '#db2777', '#16a34a', '#f59e0b', '#0369a1'];

const channelColor = (channel: string, index: number) => (
  CHANNEL_COLORS[channel] || PALETTE[index % PALETTE.length]
);

const CountList: React.FC<{
  rows: Array<{ name: string; count: number; color?: string }>;
  empty: string;
}> = ({ rows, empty }) => {
  if (!rows.length) return <p className="store-dash-hint">{empty}</p>;
  return (
    <div className="store-dash-legend">
      {rows.map((row, index) => (
        <div key={row.name} className="store-dash-legend-item">
          <i style={{ background: row.color || PALETTE[index % PALETTE.length] }} />
          <span>{row.name}</span>
          <em>{row.count.toLocaleString()}</em>
        </div>
      ))}
    </div>
  );
};

const StoreDashboardSupportView: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<SupportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/store-dashboard/support`, {
        params: { days },
        withCredentials: true,
      });
      setData(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load support');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const needsApi = data && !data.configured;

  return (
    <div className="store-dash">
      <div className="store-dash-toolbar">
        <div>
          <div className="store-dash-kicker">{data?.account ? `Gorgias · ${data.account}` : 'Helpdesk'}</div>
          <h1>Support</h1>
        </div>
        <div className="store-dash-ranges">
          {RANGES.map((range) => (
            <button
              key={range.value}
              type="button"
              className={`store-dash-range${days === range.value ? ' is-active' : ''}`}
              onClick={() => setDays(range.value)}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {needsApi && (
        <p className="store-dash-hint">
          Connect Gorgias on the <Link to="/apis">APIs</Link> page to load ticket metrics.
        </p>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error || data?.error ? (
        <div className="alert alert-danger">{error || data?.error}</div>
      ) : (
        <>
          <div className="store-dash-cards">
            <div className="store-dash-card">
              <span>Created</span>
              <strong>{data?.created ?? 0}</strong>
            </div>
            <div className="store-dash-card">
              <span>{data?.openNow == null ? 'Still open' : 'Open now'}</span>
              <strong>{data?.openNow ?? data?.stillOpen ?? 0}</strong>
            </div>
            <div className="store-dash-card">
              <span>Closed</span>
              <strong>{data?.closed ?? 0}</strong>
            </div>
            <div className="store-dash-card">
              <span>CSAT</span>
              <strong>{percent(data?.csat.rate ?? null)}</strong>
            </div>
            <div className="store-dash-card">
              <span>Surveys</span>
              <strong>{data?.csat.surveys ?? 0}</strong>
            </div>
          </div>

          <div className="store-dash-charts">
            <section className="store-dash-chart">
              <h2>Tickets created vs closed</h2>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data?.series || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tickFormatter={formatDay} minTickGap={24} />
                  <YAxis allowDecimals={false} />
                  <Tooltip labelFormatter={formatDay} />
                  <Legend />
                  <Line type="monotone" dataKey="created" name="Created" stroke="#2563eb" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="closed" name="Closed" stroke="#16a34a" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </section>

            <div className="store-dash-fleet-grid store-dash-support-splits">
              <section className="store-dash-chart">
                <h2>Categories</h2>
                <CountList
                  rows={(data?.categories || []).map((row) => ({ name: row.category || 'Uncategorized', count: row.count }))}
                  empty="No contact reasons in this range."
                />
              </section>
              <section className="store-dash-chart">
                <h2>Tag types</h2>
                <CountList
                  rows={(data?.tagTypes || []).map((row) => ({ name: row.type || 'Untagged', count: row.count }))}
                  empty="No tags in this range."
                />
              </section>
              <section className="store-dash-chart">
                <h2>Tags</h2>
                <CountList
                  rows={(data?.tags || []).map((row) => ({ name: row.tag || 'Untagged', count: row.count }))}
                  empty="No tags in this range."
                />
              </section>
              <section className="store-dash-chart">
                <h2>Channels</h2>
                <CountList
                  rows={(data?.channels || []).map((row) => ({
                    name: formatChannel(row.channel || 'other'),
                    count: row.count,
                    color: channelColor(row.channel || 'other', 0),
                  }))}
                  empty="No tickets in this range."
                />
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StoreDashboardSupportView;
